import { memo, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";

interface CommissionPeriodFilterProps {
  startDate: string;
  endDate: string;
  loading: boolean;
  onApply: (period: { startDate: string; endDate: string }) => void;
  idPrefix: string;
}

/**
 * Filtro de período (data inicial / data final) com Apply explícito.
 *
 * Mantém um draft local para evitar refetches a cada keystroke. O
 * `idPrefix` torna os ids dos inputs únicos quando o filtro é usado em
 * mais de um contexto (tela principal e modal de detalhes).
 *
 * @example
 * <CommissionPeriodFilter
 *   idPrefix="commission-summary"
 *   startDate={summary.period.startDate}
 *   endDate={summary.period.endDate}
 *   loading={summary.loading}
 *   onApply={summary.setPeriod}
 * />
 */
export const CommissionPeriodFilter = memo(function CommissionPeriodFilter({
  startDate,
  endDate,
  loading,
  onApply,
  idPrefix,
}: CommissionPeriodFilterProps) {
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);

  useEffect(() => {
    setDraftStart(startDate);
  }, [startDate]);

  useEffect(() => {
    setDraftEnd(endDate);
  }, [endDate]);

  const isInvalid =
    draftStart === "" || draftEnd === "" || draftEnd < draftStart;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-start`}>Data Inicial</Label>
        <Input
          id={`${idPrefix}-start`}
          type="date"
          value={draftStart}
          onChange={(e) => setDraftStart(e.target.value)}
          disabled={loading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-end`}>Data Final</Label>
        <Input
          id={`${idPrefix}-end`}
          type="date"
          value={draftEnd}
          onChange={(e) => setDraftEnd(e.target.value)}
          disabled={loading}
        />
      </div>

      <Button
        onClick={() => onApply({ startDate: draftStart, endDate: draftEnd })}
        disabled={loading || isInvalid}
        className="gap-2"
      >
        <Search className="w-4 h-4" />
        Aplicar
      </Button>
    </div>
  );
});
